import { useMemo } from 'react'
import { Link } from '@tanstack/react-router'
import { AdminLayout } from '~/components/AdminLayout'
import { useAllOrders } from '~/api/admin-orders'
import type { Order } from '~/api/orders'
import styles from './dashboard.module.css'

/**
 * Get the status style class for an order
 */
function getStatusClass(status: string): string {
  switch (status) {
    case 'PENDING':
      return styles.statusPending
    case 'IN_TRANSIT':
      return styles.statusInTransit
    case 'DELIVERED':
      return styles.statusDelivered
    case 'CANCELLED':
      return styles.statusCancelled
    default:
      return ''
  }
}

/**
 * Format amount in IDR currency
 */
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

/**
 * Format date to readable string
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

/**
 * Check if a date is today
 */
function isToday(dateString: string): boolean {
  const date = new Date(dateString)
  const today = new Date()
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  )
}

interface DashboardStats {
  totalOrders: number
  pendingOrders: number
  inTransitOrders: number
  deliveredToday: number
  totalRevenue: number
  recentOrders: Order[]
}

/**
 * Calculate dashboard statistics from orders
 */
function calculateStats(orders: Order[]): DashboardStats {
  let pendingOrders = 0
  let inTransitOrders = 0
  let deliveredToday = 0
  let totalRevenue = 0

  for (const order of orders) {
    // Sum up total revenue from all orders
    totalRevenue += order.totalAmount

    switch (order.status) {
      case 'PENDING':
        pendingOrders++
        break
      case 'IN_TRANSIT':
        inTransitOrders++
        break
      case 'DELIVERED':
        if (isToday(order.updatedAt)) {
          deliveredToday++
        }
        break
    }
  }

  // Get last 5 orders sorted by createdAt (most recent first)
  const recentOrders = [...orders]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 5)

  return {
    totalOrders: orders.length,
    pendingOrders,
    inTransitOrders,
    deliveredToday,
    totalRevenue,
    recentOrders,
  }
}

export function AdminDashboardPage() {
  const { data: orders, isLoading, isError, error } = useAllOrders()

  const stats = useMemo(() => {
    if (!orders) return null
    return calculateStats(orders)
  }, [orders])

  if (isLoading) {
    return (
      <AdminLayout title="DASHBOARD">
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner} />
          <span className={styles.loadingText}>Loading dashboard...</span>
        </div>
      </AdminLayout>
    )
  }

  if (isError) {
    return (
      <AdminLayout title="DASHBOARD">
        <div className={styles.errorContainer}>
          <span className={styles.errorText}>
            Error: {error?.message || 'Failed to load dashboard data'}
          </span>
        </div>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout title="DASHBOARD">
      <div className={styles.dashboard}>
        {/* Stats Grid */}
        <section>
          <div className={styles.statsGrid}>
            <div className={styles.statCard}>
              <span className={styles.statNumber}>{stats?.totalOrders ?? 0}</span>
              <span className={styles.statLabel}>Total Orders</span>
            </div>
            <div className={styles.statCard}>
              <span className={`${styles.statNumber} ${styles.statNumberAccent}`}>
                {stats?.pendingOrders ?? 0}
              </span>
              <span className={styles.statLabel}>Pending Orders</span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statNumber}>{stats?.inTransitOrders ?? 0}</span>
              <span className={styles.statLabel}>In Transit</span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statNumber}>{stats?.deliveredToday ?? 0}</span>
              <span className={styles.statLabel}>Delivered Today</span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statNumber}>
                {stats ? formatCurrency(stats.totalRevenue) : 'Rp 0'}
              </span>
              <span className={styles.statLabel}>Total Revenue</span>
            </div>
          </div>
        </section>

        {/* Quick Actions */}
        <section>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Quick Actions</h2>
          </div>
          <div className={styles.quickActions}>
            <Link to="/admin/orders" className={`${styles.actionButton} ${styles.actionButtonPrimary}`}>
              View All Orders
            </Link>
            <Link to="/admin/drivers" className={styles.actionButton}>
              View Drivers
            </Link>
          </div>
        </section>

        {/* Recent Orders */}
        <section className={styles.recentOrders}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Recent Orders</h2>
            <Link to="/admin/orders" className={styles.sectionLink}>
              View All →
            </Link>
          </div>
          {stats?.recentOrders && stats.recentOrders.length > 0 ? (
            <div className={styles.ordersList}>
              {stats.recentOrders.map((order) => (
                <Link
                  key={order.id}
                  to="/admin/orders/$orderId"
                  params={{ orderId: order.id }}
                  className={styles.orderItem}
                >
                  <div className={styles.orderInfo}>
                    <span className={styles.orderId}>{order.externalId}</span>
                    <span className={styles.orderDate}>{formatDate(order.createdAt)}</span>
                  </div>
                  <span className={`${styles.orderStatus} ${getStatusClass(order.status)}`}>
                    {order.status.replace('_', ' ')}
                  </span>
                  <span className={styles.orderAmount}>{formatCurrency(order.totalAmount)}</span>
                </Link>
              ))}
            </div>
          ) : (
            <div className={styles.emptyState}>
              <span className={styles.emptyText}>No orders yet</span>
            </div>
          )}
        </section>
      </div>
    </AdminLayout>
  )
}
